import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Stack, Button, Chip, Divider,
} from '@mui/material';
import * as XLSX from 'xlsx';
import PopoverFiltroRelatorio from './PopoverFiltroRelatorio';
import generatePdfRelatorioDespesas from './EditPdfRelatorioDespesas';
import { cores } from '../styles/styleUtils';

// ── Paleta TriAxis — aliases dos tokens de styleUtils ─────────────────────
const LARANJA = cores.laranja;
const CINZA   = cores.chumbo;

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── Chip de status ───────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const mapa = {
    Pago:      { bg: '#E6F4EA', cor: '#1E7E34' },
    'A pagar': { bg: '#EEEEEE', cor: '#555555' },
    Vencido:   { bg: '#FDECEA', cor: '#C62828' },
  };
  const s = mapa[status] || mapa['A pagar'];
  return (
    <Chip
      label={status}
      size="small"
      sx={{ backgroundColor: s.bg, color: s.cor, fontWeight: 600, fontSize: 11 }}
    />
  );
};

export default function RelatorioDespesas({ data, modo = 'obras', type, filtro, setFiltro, onLimpar }) {
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro = () => setOpenFiltro(null);
  const limparFiltro  = () => { setOpenFiltro(false); onLimpar(); };
  const onConfirmFiltro = (f) => { setOpenFiltro(false); setFiltro(f); };

  if (!data) return null;

  const ehObras = modo === 'obras';
  const colNome = ehObras ? 'Obra' : 'Centro de Custo';
  const campoNome = ehObras ? 'obra' : 'centroDeCusto';

  const dados  = data.dados || [];
  const resumo = data.resumo || [];
  const totais = data.totais || { aPagar: 0, pago: 0, vencido: 0, total: 0 };

  // ── Agrupa as linhas por unidade ────────────────────────────────────────
  const porUnidade = dados.reduce((acc, l) => {
    const u = l.unidade || '—';
    if (!acc[u]) acc[u] = [];
    acc[u].push(l);
    return acc;
  }, {});
  const unidades = Object.keys(porUnidade).sort();

  // ── Exportar Excel ──────────────────────────────────────────────────────
  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheet = [];
    sheet.push([`Relatório de Despesas — ${ehObras ? 'Obras' : 'Operacionais'}`]);
    sheet.push([]);

    unidades.forEach((u) => {
      sheet.push([`Unidade: ${u}`]);
      sheet.push(['Data', 'Descrição', colNome, 'Status', 'Data Ref.', 'Valor']);
      porUnidade[u].forEach((l) => {
        sheet.push([l.data, l.descricao, l[campoNome], l.status, l.dataRef, l.valor]);
      });
      sheet.push([]);
    });

    sheet.push([`2. Resumo por ${colNome}`]);
    sheet.push([colNome, 'A Pagar', 'Pago', 'Vencido', 'Total', '% do Total']);
    resumo.forEach((r) => {
      sheet.push([r.nome, r.aPagar, r.pago, r.vencido, r.total, Number(r.percentual || 0).toFixed(2)]);
    });
    sheet.push(['Total Geral', totais.aPagar, totais.pago, totais.vencido, totais.total, '100']);

    const ws = XLSX.utils.aoa_to_sheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, ehObras ? 'Despesas Obras' : 'Despesas Operac');
    XLSX.writeFile(wb, `relatorio-despesas-${ehObras ? 'obras' : 'operacionais'}.xlsx`);
  };

  return (
    <Box mt={3}>
      <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => setOpenFiltro(true)}>Filtro</Button>
        <Button variant="outlined" color="secondary" onClick={() => generatePdfRelatorioDespesas(data, filtro, modo)}>
          Exportar em PDF
        </Button>
        <Button variant="contained" sx={{ backgroundColor: LARANJA, '&:hover': { backgroundColor: cores.laranjaEscuro } }} onClick={exportarExcel}>
          Download Excel
        </Button>
      </Stack>

      {/* KPIs */}
      <Stack direction="row" gap={1.5} flexWrap="wrap" mb={3}>
        {[
          { label: 'Lançamentos', valor: String(dados.length), cor: CINZA },
          { label: 'A Pagar', valor: fmtBRL(totais.aPagar), cor: '#555555' },
          { label: 'Vencido', valor: fmtBRL(totais.vencido), cor: '#C62828' },
          { label: 'Pago', valor: fmtBRL(totais.pago), cor: '#1E7E34' },
          { label: 'Total Geral', valor: fmtBRL(totais.total), cor: LARANJA, destaque: true },
        ].map((k) => (
          <Box key={k.label} sx={{
            flex: 1, minWidth: 120, textAlign: 'center',
            border: `1px solid ${k.destaque ? LARANJA : '#DDD'}`,
            borderLeft: `4px solid ${k.cor}`, borderRadius: 1, p: 1.5,
            backgroundColor: k.destaque ? '#FFF3E0' : '#FAFAFA',
          }}>
            <Typography variant="caption" color="text.secondary" display="block">{k.label}</Typography>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: k.cor }}>{k.valor}</Typography>
          </Box>
        ))}
      </Stack>

      {/* Detalhamento por unidade */}
      {dados.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ my: 4 }}>
          Nenhuma despesa {ehObras ? 'de obra' : 'operacional'} no período selecionado.
        </Typography>
      )}

      {unidades.map((u) => {
        const linhas = porUnidade[u];
        const totalUnidade = linhas.reduce((s, l) => s + (l.valor || 0), 0);
        return (
          <Box key={u} mb={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 2 }}>
              <Box sx={{ width: 4, height: 18, backgroundColor: LARANJA, borderRadius: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold" color={CINZA}>Unidade: {u}</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{colNome}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Ref.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linhas.map((l, i) => (
                  <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF8F0' } }}>
                    <TableCell>{l.data}</TableCell>
                    <TableCell>{l.descricao}</TableCell>
                    <TableCell>{l[campoNome]}</TableCell>
                    <TableCell><StatusChip status={l.status} /></TableCell>
                    <TableCell>{l.dataRef || '—'}</TableCell>
                    <TableCell align="right">{fmtBRL(l.valor)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={5} align="right"><strong>Total {u}</strong></TableCell>
                  <TableCell align="right"><strong>{fmtBRL(totalUnidade)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        );
      })}

      {/* Resumo */}
      {resumo.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 4, height: 18, backgroundColor: LARANJA, borderRadius: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" color={CINZA}>Resumo por {colNome}</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>{colNome}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">A Pagar</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Vencido</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Pago</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">% do Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resumo.map((r, i) => (
                <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF8F0' } }}>
                  <TableCell>{r.nome}</TableCell>
                  <TableCell align="right">{fmtBRL(r.aPagar)}</TableCell>
                  <TableCell align="right" sx={{ color: r.vencido > 0 ? '#C62828' : 'inherit' }}>{fmtBRL(r.vencido)}</TableCell>
                  <TableCell align="right" sx={{ color: '#1E7E34' }}>{fmtBRL(r.pago)}</TableCell>
                  <TableCell align="right">{fmtBRL(r.total)}</TableCell>
                  <TableCell align="right">{Number(r.percentual || 0).toFixed(2)}%</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><strong>Total Geral</strong></TableCell>
                <TableCell align="right"><strong>{fmtBRL(totais.aPagar)}</strong></TableCell>
                <TableCell align="right"><strong>{fmtBRL(totais.vencido)}</strong></TableCell>
                <TableCell align="right"><strong>{fmtBRL(totais.pago)}</strong></TableCell>
                <TableCell align="right"><strong>{fmtBRL(totais.total)}</strong></TableCell>
                <TableCell align="right"><strong>100%</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      )}

      {openFiltro && (
        <PopoverFiltroRelatorio
          filtro={filtro}
          open={openFiltro}
          onClose={onCloseFiltro}
          onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro}
          type={type}
        />
      )}
    </Box>
  );
}